import { hashKey } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

/**
 * Campos de `useQuery` usados para saber se o total de páginas foi confirmado
 * pela resposta da consulta vigente (contrato CT02).
 */
export type PaginationQueryState = {
	isSuccess?: boolean;
	isFetched?: boolean;
	isFetching?: boolean;
	isPlaceholderData?: boolean;
	dataUpdatedAt?: number;
};

export type UsePaginationTotalOptions = {
	/** Página solicitada pelo consumidor, com índice inicial 1. */
	page: number;
	/** Atualiza a página do consumidor. Só é chamado para recuperar uma página válida. */
	onPageChange: (page: number) => void;
	/**
	 * Critérios aplicados e tamanho da página, sem o número da página. Quando
	 * mudam, o total confirmado anterior deixa de valer para a nova consulta.
	 */
	criteria: unknown;
	/** Total de páginas informado pela resposta atual (não o total de registros). */
	totalPages: unknown;
	/** Resultado de `useQuery` da página solicitada. */
	query: PaginationQueryState;
};

export type PaginationTotal = {
	/**
	 * Total para o rodapé: o da resposta confirmada da página solicitada ou, na
	 * troca de página com os mesmos critérios, o último total confirmado.
	 * `null` quando ainda não existe total confiável para esses critérios.
	 */
	total: number | null;
	/** Se `total` veio da resposta confirmada da página solicitada. */
	isConfirmed: boolean;
};

export const isKnownPageTotal = (value: unknown): value is number =>
	typeof value === "number" && Number.isSafeInteger(value) && value >= 0;

/**
 * Resposta real da consulta vigente: sucesso, já buscada (descarta
 * `initialData` sintético) e sem dados de outra chave (`keepPreviousData`).
 */
export const isConfirmedQueryResponse = (query: PaginationQueryState) =>
	Boolean(query.isSuccess && query.isFetched && !query.isPlaceholderData);

/**
 * Integra o total de páginas de uma consulta paginada ao estado do consumidor.
 *
 * - Separa total confirmado, total provisório (mesmos critérios, outra página)
 *   e total desconhecido.
 * - Depois de uma resposta confirmada da página solicitada que reduz o total
 *   abaixo dela, volta uma única vez para o novo limite (contrato CT14). Total
 *   provisório, `initialData`, erro ou consulta em andamento nunca corrigem a
 *   página.
 *
 * O consumidor continua dono da página e dos critérios; o hook não inicia
 * consultas nem altera filtros.
 */
export function usePaginationTotal({
	page,
	onPageChange,
	criteria,
	totalPages,
	query,
}: UsePaginationTotalOptions): PaginationTotal {
	const criteriaKey = hashKey([criteria]);
	const isConfirmed =
		isConfirmedQueryResponse(query) && isKnownPageTotal(totalPages);

	const lastConfirmedRef = useRef<{ key: string; total: number } | null>(null);
	if (isConfirmed) {
		lastConfirmedRef.current = { key: criteriaKey, total: totalPages };
	}
	const lastConfirmed = lastConfirmedRef.current;
	let total: number | null = null;
	if (isConfirmed) {
		total = totalPages;
	} else if (lastConfirmed?.key === criteriaKey) {
		total = lastConfirmed.total;
	}

	const onPageChangeRef = useRef(onPageChange);
	useEffect(() => {
		onPageChangeRef.current = onPageChange;
	}, [onPageChange]);

	// Uma correção por resposta confirmada: rerenderizar a mesma resposta não
	// inicia outra transição.
	const reconciledResponseRef = useRef<string | null>(null);
	const canReconcile = isConfirmed && !query.isFetching;
	const confirmedLimit = isConfirmed ? Math.max(1, totalPages) : null;
	const responseId = `${criteriaKey}|${page}|${query.dataUpdatedAt ?? 0}`;

	useEffect(() => {
		if (!canReconcile || confirmedLimit === null || page <= confirmedLimit) {
			return;
		}
		if (reconciledResponseRef.current === responseId) {
			return;
		}
		reconciledResponseRef.current = responseId;
		onPageChangeRef.current(confirmedLimit);
	}, [canReconcile, confirmedLimit, page, responseId]);

	return { total, isConfirmed };
}
